import * as React from 'react';
import type { IdentityOcrResult } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { QrKod } from '@/components/ui/qr';
import { ApiHatasi, api, oturum } from '@/lib/api';

/**
 * Masa tarafi: "Telefonla tara".
 *
 * Neden var: resepsiyon bilgisayarinin kamerasi ya yoktur ya da kimligi
 * okuyacak kadar iyi degildir. Gorevlinin telefonu ise hep yanindadir.
 *
 * Neden ISTEGE BAGLI: surekli acik bir telefon kanali, KVKK acisindan
 * savunulamaz bir sey olurdu - kimlik fotograflari kimsenin istemedigi
 * anlarda kisisel cihazlardan gecerdi. Bu yuzden kanal yalnizca gorevli
 * "Telefonla tara" dedikce acilir, ekran kapaninca da olur.
 *
 * Telefon check-in'i TAMAMLAYAMAZ: yalnizca okunmus kimlik alanlarini
 * masaya birakir. Oda, ucret ve KBS bildirimi masada kalir.
 */

interface Props {
  reservationId: string;
  rezKodu: string;
  odaNo: string | null;
  kisiSayisi: number;
  /** Masanin su an ustunde oldugu misafir (0 tabanli). */
  sira: number;
  tenantId: string;
  /** Telefondan kimlik gelince: hangi misafire, hangi sonuc. */
  onKimlik: (sira: number, sonuc: IdentityOcrResult) => void;
}

interface Eslesme {
  eslesmeId: string;
  kod: string;
  kodSonKullanma: number;
}

export function TelefonEslesme({
  reservationId,
  rezKodu,
  odaNo,
  kisiSayisi,
  sira,
  tenantId,
  onKimlik,
}: Props) {
  const [acik, setAcik] = React.useState(false);
  const [eslesme, setEslesme] = React.useState<Eslesme | null>(null);
  const [baglandi, setBaglandi] = React.useState(false);
  const [kalanSn, setKalanSn] = React.useState(0);
  const [hata, setHata] = React.useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(false);
  const [sonGelen, setSonGelen] = React.useState<number | null>(null);

  const eslesmeIdRef = React.useRef<string | null>(null);
  eslesmeIdRef.current = eslesme?.eslesmeId ?? null;

  async function eslesmeAc() {
    setYukleniyor(true);
    setHata(null);
    try {
      const veri = await api<Eslesme>('/api/tarayici/oturum', {
        method: 'POST',
        body: JSON.stringify({ reservationId, rezKodu, odaNo, kisiSayisi, sira }),
      });
      setEslesme(veri);
      setBaglandi(false);
      setAcik(true);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Eşleşme açılamadı.');
    } finally {
      setYukleniyor(false);
    }
  }

  /** Kanali kapat. Telefon ekrani da aninda kilitlenir. */
  const kapat = React.useCallback(async (id: string | null) => {
    if (!id) return;
    await api(`/api/tarayici/oturum/${id}`, { method: 'DELETE' }).catch(() => {});
  }, []);

  /*
   * Check-in ekrani kapaninca eslesme de olur.
   *
   * Bu temizlik olmasa masada unutulan bir QR ile telefon 15 dakika daha
   * kimlik gonderebilirdi; ustelik o kimlikler artik kimsenin baktigi bir
   * ekrana dusmezdi.
   */
  React.useEffect(() => {
    return () => {
      void kapat(eslesmeIdRef.current);
    };
  }, [kapat]);

  // QR sayaci: kod 60 saniye yasar, bittiginde yenisi istenir.
  React.useEffect(() => {
    if (!eslesme || baglandi) return;
    const tik = () => {
      const kalan = Math.max(0, Math.ceil((eslesme.kodSonKullanma - Date.now()) / 1000));
      setKalanSn(kalan);
    };
    tik();
    const z = setInterval(tik, 500);
    return () => clearInterval(z);
  }, [eslesme, baglandi]);

  // Masa hangi misafirdeyse telefon da orada dursun.
  React.useEffect(() => {
    if (!eslesme || !baglandi) return;
    void api(`/api/tarayici/oturum/${eslesme.eslesmeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ sira }),
    }).catch(() => {});
  }, [sira, eslesme, baglandi]);

  /*
   * Canli yayin. Kimlik verisi bu kanaldan GECMEZ - yayin tesisteki tum
   * personel ekranlarina gider. Yalnizca "hazir" sinyali gelir, veriyi
   * kimlik dogrulamali ayri bir istekle celeriz ve o an sunucudan silinir.
   */
  React.useEffect(() => {
    if (!eslesme) return;
    const token = oturum.token();
    if (!token) return;

    const ws = new WebSocket(
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${tenantId}?token=${token}`,
    );
    ws.onmessage = async (olay) => {
      const m = JSON.parse(olay.data as string) as {
        tip: string;
        eslesmeId?: string;
        acik?: boolean;
      };
      if (m.eslesmeId !== eslesme.eslesmeId) return;

      if (m.tip === 'TARAYICI_DURUM') {
        if (m.acik === false) {
          setEslesme(null);
          setBaglandi(false);
          return;
        }
        setBaglandi(true);
      }
      if (m.tip === 'TARAYICI_KIMLIK') {
        try {
          const veri = await api<{ sira: number | null; ocr: IdentityOcrResult | null }>(
            `/api/tarayici/oturum/${eslesme.eslesmeId}/kimlik`,
          );
          if (veri.ocr && veri.sira !== null) {
            onKimlik(veri.sira, veri.ocr);
            setSonGelen(veri.sira);
            setTimeout(() => setSonGelen(null), 3500);
          }
        } catch {
          setHata('Telefondan gelen kimlik alınamadı. Tekrar gönderin.');
        }
      }
    };
    const ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [eslesme, tenantId, onKimlik]);

  if (!acik || !eslesme) {
    // Kapali hali bir "secenek satiri"dir: neden var oldugunu soylemeden
    // duran yalniz bir dugme, ekranda ne ise yaradigi belirsiz kaliyordu.
    return (
      <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-slate-800">Masadaki kamera yetersiz mi?</p>
          <p className="text-[12.5px] text-slate-500">
            Kendi telefonunuzu kamera olarak kullanın. Kimlik buraya düşer, check-in yine burada tamamlanır.
          </p>
        </div>
        <Button
          varyant="ikincil"
          onClick={eslesmeAc}
          yukleniyor={yukleniyor}
          className="w-full shrink-0 sm:w-auto"
        >
          Telefonla tara
        </Button>
        {hata && <span className="text-[13px] text-red-700">{hata}</span>}
      </div>
    );
  }

  const adres = `${location.origin}/tarayici#${eslesme.kod}`;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      {baglandi ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
            ✓
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900">Telefon bağlandı</p>
            <p className="text-[13px] text-slate-600">
              Telefondaki ekran {sira + 1}. misafiri gösteriyor. Kimliği orada okutun, alanlar buraya düşecek.
            </p>
          </div>
          <Button
            varyant="sessiz"
            boyut="md"
            className="sm:ml-auto"
            onClick={() => {
              void kapat(eslesme.eslesmeId);
              setEslesme(null);
              setBaglandi(false);
              setAcik(false);
            }}
          >
            Bağlantıyı kes
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="shrink-0 self-center rounded-xl bg-white p-2 shadow-sm">
            <QrKod deger={adres} boyut={128} />
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <p className="font-semibold text-slate-900">Telefonunuzun kamerasıyla okutun</p>
            <p className="mt-0.5 text-[13px] text-slate-600">
              Telefonda otelcms oturumunuz açık olmalı. Kare kod{' '}
              <strong className={kalanSn <= 10 ? 'text-red-700' : undefined}>{kalanSn} sn</strong> geçerli ve
              yalnızca bir telefon bağlanabilir.
            </p>
            <p className="mt-1 text-[12px] text-slate-500">
              Kod: <span className="font-mono tracking-[0.2em]">{eslesme.kod}</span>
            </p>
            {kalanSn === 0 && (
              <Button varyant="ikincil" boyut="md" className="mt-2" onClick={eslesmeAc} yukleniyor={yukleniyor}>
                Yeni kod üret
              </Button>
            )}
          </div>
        </div>
      )}

      {sonGelen !== null && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-800">
          {sonGelen + 1}. misafirin kimliği telefondan geldi.
        </p>
      )}
      {hata && <p className="text-[13px] text-red-700">{hata}</p>}
    </div>
  );
}
